import {useEffect, useMemo, useRef, useState} from "react";
import axios from "axios";
import Spinner from "@/Components/Spinner";
import {faChevronRight, faCreditCard} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import toast from "react-hot-toast";
import {PaymentStatus} from "@/types/enums";
import {fetchPaymentStatus, isSettled, waitForPaymentSettlement} from "@/services/paymentStatusService";
import {MultiViewModal} from "@/Components/MultiViewModal";

const MAX_HOURS_PER_ORDER = 2000;

function RadioDot({selected}: {selected: boolean}) {
    return (
        <span
            aria-hidden="true"
            className={`h-3.5 w-3.5 shrink-0 rounded-full transition-all peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-white dark:peer-focus-visible:ring-offset-gray-800 ${
                selected
                    ? 'border-4 border-blue-600 bg-white dark:border-blue-500 dark:bg-gray-800'
                    : 'border border-gray-300 dark:border-gray-500'
            }`}
        />
    );
}

export type PurchaseModalProps = {
    show: boolean;
    onClose: () => void;
    prices?: Record<string, number>;
    onCompleted?: () => void;
    message?: string;
};

export default function PurchaseModal({show, onClose, prices: initPrices, message, onCompleted}: PurchaseModalProps) {
    const [prices, setPrices] = useState(initPrices);
    const [selectedHours, setSelectedHours] = useState(10);
    const [customHours, setCustomHours] = useState<string | null>(null);
    const [lastCustomValue, setLastCustomValue] = useState<string>('');
    const [submitting, setIsSubmitting] = useState('');
    const [currentStep, setCurrentStep] = useState(0);
    const customInputRef = useRef<HTMLInputElement>(null);
    const isAsync = onCompleted !== undefined;

    const tierHours = Object.keys(prices || {}).map(Number);
    const maxTier = Math.max(...tierHours);

    const getHourRate = (hours: number) => {
        if (!prices) return 0;
        const tiers = [...tierHours].sort((a, b) => b - a);
        return prices[tiers.find(tier => hours >= tier) || Math.min(...tiers)];
    };

    const effectiveHours = customHours ? parseInt(customHours) : selectedHours;
    const exceedsMaxHours = customHours !== null && parseInt(customHours) > MAX_HOURS_PER_ORDER;

    const total = useMemo(() => {
        if (customHours !== null) {
            const hours = parseInt(customHours);
            if (!isNaN(hours) && hours > 0) {
                return hours * getHourRate(hours);
            }
            return 0;
        }
        return prices ? selectedHours * prices[selectedHours] : 0;
    }, [selectedHours, prices, customHours]);

    const getPrices = async () => {
        const result = await axios.get(route('credit.pricing'));
        setPrices(result.data.pricing);
    }

    useEffect(() => {
        if (show && !prices) {
            getPrices();
        }
    }, [show]);

    const initPaymentSession = async (payment_type: string) => {
        const minHours = Math.min(...Object.keys(prices || {}).map(Number))
        if (customHours && (isNaN(parseInt(customHours)) || parseInt(customHours) < minHours)) {
            toast.error('Custom hours must be at least ' + minHours);
            return;
        }

        setIsSubmitting(payment_type)
        const origin = window.location.pathname + window.location.search;
        const params = {
            hours: effectiveHours,
            origin,
            "async": isAsync,
            payment_type: payment_type,
            custom_rate: !!customHours
        };

        try {
            const result = await axios.post(route('payment.charge'), params);
            const {url, session_id} = result.data;
            if (isAsync) {
                let childWindow = window.open(url, '_blank');
                if (childWindow) {
                    await waitForPaymentFlow(childWindow);
                    setIsSubmitting('')

                    if (await fetchPaymentStatus(session_id) === PaymentStatus.CREATED) {
                        return;
                    }

                    const toastId = toast.loading('Confirming payment...');
                    const status = await waitForPaymentSettlement(session_id);

                    if (status === PaymentStatus.COMPLETED) {
                        toast.success('Payment completed', {id: toastId});
                        onCompleted();
                    } else if (isSettled(status)) {
                        toast.error('Payment was not completed', {id: toastId});
                    } else {
                        toast('Payment is pending. It will be credited once confirmed.', {icon: '⏳', id: toastId});
                    }
                } else {
                    window.location.href = url;
                }
            } else {
                window.location.href = url;
            }
        } catch (error: any) {
            const message = error?.response?.data?.message || 'An error occurred while initializing the payment';
            toast.error(message);
            setIsSubmitting('');
        }
    }

    const waitForPaymentFlow = async (childWindow: Window): Promise<void> => {
        return new Promise((resolve) => {
            const interval = setInterval(() => {
                if (childWindow.closed) {
                    clearInterval(interval);
                    resolve()
                }
            }, 1000);
        })
    }

    const handleCustomHoursChange = (value: string) => {
        setCustomHours(value);
        setLastCustomValue(value);
        setSelectedHours(0); // Clear preset selection
    }

    const handleCustomHoursBlur = () => {
        if (customHours === '') return;

        const minHours = Math.min(...Object.keys(prices || {}).map(Number));
        const numValue = parseInt(customHours || '0');

        if (isNaN(numValue) || numValue < minHours) {
            setCustomHours(minHours.toString());
        }
    }

    useEffect(() => {
        setIsSubmitting('')
    }, [show]);

    useEffect(() => {
        if (customHours !== null && customInputRef.current) {
            customInputRef.current.focus();
        }
    }, [customHours]);

    const optionRowClass = (selected: boolean) =>
        `flex items-center gap-3 px-4 py-3 transition-colors ${
            selected
                ? 'bg-blue-100/20 dark:bg-blue-900/30'
                : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
        }`;

    const baseRate = prices ? prices[Math.min(...tierHours)] : 0;
    const savingsPercent = (rate: number) => baseRate ? Math.round((1 - rate / baseRate) * 100) : 0;

    const SelectHoursView = (
        <div className="bg-white dark:bg-gray-800 mt-2 px-6 pb-6 rounded-lg w-full mx-auto">
            <p className="mb-4 text-center text-sm tabular-nums text-gray-500 dark:text-gray-400">
                <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">${total.toFixed(2)}</span>
                {' '}· {effectiveHours || 0} hours at ${getHourRate(effectiveHours || 0).toFixed(2)}/hr
            </p>

            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/70">
                {Object.keys(prices || {}).map((key) => {
                    const selected = selectedHours === parseInt(key) && customHours === null;
                    const rate = prices![key];
                    const savings = savingsPercent(rate);
                    return (
                        <label key={key} className={optionRowClass(selected)}>
                            <input
                                checked={selected}
                                onChange={() => {
                                    if (customHours !== null) {
                                        setLastCustomValue(customHours);
                                    }
                                    setSelectedHours(parseInt(key));
                                    setCustomHours(null);
                                }}
                                type="radio"
                                name="priceOption"
                                value={key}
                                className="peer sr-only"
                            />
                            <RadioDot selected={selected}/>
                            <span className={`grow text-sm font-medium ${selected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-gray-100'}`}>
                                {key} hours
                            </span>
                            {savings > 0 && (
                                <span className="text-xs font-medium text-green-600 dark:text-green-400">Save {savings}%</span>
                            )}
                            <span className="w-16 text-right text-sm tabular-nums text-gray-500 dark:text-gray-400">
                                ${rate.toFixed(2)}/hr
                            </span>
                        </label>
                    );
                })}

                <label className={optionRowClass(customHours !== null)}>
                    <input
                        checked={customHours !== null}
                        onChange={() => {
                            if (customHours === null) {
                                setCustomHours(lastCustomValue || Math.min(...tierHours).toString());
                                setSelectedHours(0);
                            }
                        }}
                        type="radio"
                        name="priceOption"
                        className="peer sr-only"
                    />
                    <RadioDot selected={customHours !== null}/>
                    {customHours === null ? (
                        <>
                            <span className="grow text-sm text-gray-500 dark:text-gray-400">Custom amount</span>
                            <FontAwesomeIcon icon={faChevronRight} className="w-2 text-gray-300 dark:text-gray-500"/>
                        </>
                    ) : (
                        <>
                            <span className="flex grow items-center gap-2">
                                <input
                                    type="number"
                                    ref={customInputRef}
                                    value={customHours}
                                    onChange={(e) => handleCustomHoursChange(e.target.value)}
                                    onBlur={handleCustomHoursBlur}
                                    min={Math.min(...tierHours)}
                                    max={MAX_HOURS_PER_ORDER}
                                    placeholder="Hours"
                                    aria-label="Custom hours"
                                    className={`w-20 rounded-md border bg-white px-2 py-1 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 ${exceedsMaxHours ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-gray-600'}`}
                                />
                                <span className="text-sm font-medium text-blue-700 dark:text-blue-300">hours</span>
                            </span>
                            <span className="w-16 text-right text-sm tabular-nums text-gray-500 dark:text-gray-400">
                                ${getHourRate(parseInt(customHours) || 0).toFixed(2)}/hr
                            </span>
                        </>
                    )}
                </label>
            </div>

            {exceedsMaxHours && (
                <p className="mt-2 px-1 text-xs text-red-600 dark:text-red-400">
                    Maximum {MAX_HOURS_PER_ORDER.toLocaleString()} hours per order
                </p>
            )}

            {customHours !== null && prices && parseInt(customHours) > maxTier && (
                <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-700/30 px-3 py-2">
                    <p className="text-xs text-gray-600 dark:text-gray-300">
                        Buying in bulk regularly?{' '}
                        <a
                            href={route('contact')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                            Contact us
                        </a>{' '}
                        for custom pricing
                    </p>
                </div>
            )}

            <button
                onClick={() => setCurrentStep(1)}
                disabled={exceedsMaxHours}
                className="w-full mt-5 bg-blue-600 text-white py-2.5 text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-opacity-50 dark:bg-blue-700 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600 dark:disabled:hover:bg-blue-700">
                Continue
            </button>
        </div>
    );

    const renderPaymentTile = (type: string, label: string, icon: JSX.Element, iconClassName: string, description?: string) => (
        <button
            type="button"
            disabled={submitting !== ''}
            onClick={() => initPaymentSession(type)}
            className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-gray-700/40">
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${iconClassName}`}>
                {icon}
            </span>
            <span className="grow">
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">{label}</span>
                {description && (
                    <span className="block text-xs text-gray-500 dark:text-gray-400">{description}</span>
                )}
            </span>
            {submitting === type
                ? <Spinner/>
                : <FontAwesomeIcon icon={faChevronRight} className="w-2 text-gray-300 dark:text-gray-500"/>}
        </button>
    );

    const PaymentMethodView = (
        <div className="bg-white dark:bg-gray-800 mt-3 mb-1 p-6 rounded-lg w-full mx-auto">
            <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/70">
                {renderPaymentTile(
                    'stripe',
                    'Card',
                    <FontAwesomeIcon icon={faCreditCard} className="h-4 w-4"/>,
                    'bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
                    'Klarna, Cash App Pay and more'
                )}
                {renderPaymentTile(
                    'paypal',
                    'PayPal',
                    <svg className="h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="paypal" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512"><path fill="currentColor" d="M111.4 295.9c-3.5 19.2-17.4 108.7-21.5 134-.3 1.8-1 2.5-3 2.5H12.3c-7.6 0-13.1-6.6-12.1-13.9L58.8 46.6c1.5-9.6 10.1-16.9 20-16.9 152.3 0 165.1-3.7 204 11.4 60.1 23.3 65.6 79.5 44 140.3-21.5 62.6-72.5 89.5-140.1 90.3-43.4 .7-69.5-7-75.3 24.2zM357.1 152c-1.8-1.3-2.5-1.8-3 1.3-2 11.4-5.1 22.5-8.8 33.6-39.9 113.8-150.5 103.9-204.5 103.9-6.1 0-10.1 3.3-10.9 9.4-22.6 140.4-27.1 169.7-27.1 169.7-1 7.1 3.5 12.9 10.6 12.9h63.5c8.6 0 15.7-6.3 17.4-14.9 .7-5.4-1.1 6.1 14.4-91.3 4.6-22 14.3-19.7 29.3-19.7 71 0 126.4-28.8 142.9-112.3 6.5-34.8 4.6-71.4-23.8-92.6z"></path></svg>,
                    'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
                    'Balance or linked card'
                )}
                {renderPaymentTile(
                    'crypto',
                    'Crypto',
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" className="h-4 w-4" aria-hidden="true"><path fillRule="evenodd" clipRule="evenodd" d="M8 16C12.4183 16 16 12.4183 16 8C16 3.58172 12.4183 0 8 0C3.58172 0 0 3.58172 0 8C0 12.4183 3.58172 16 8 16ZM6.62524 3C6.28007 3 6.00024 3.27982 6.00024 3.625V4.5359C5.57204 4.78312 5.19037 5.10933 4.87712 5.50078C4.31245 6.20641 4.00338 7.08247 4.00027 7.98622C3.99715 8.88996 4.30018 9.76813 4.85997 10.4776C5.17629 10.8786 5.56406 11.2123 6.00024 11.4641V12.375C6.00024 12.7202 6.28007 13 6.62524 13C6.97042 13 7.25024 12.7202 7.25024 12.375V11.9291C7.7481 12.0241 8.25696 12.0232 8.75 11.9291V12.375C8.75 12.7202 9.02982 13 9.375 13C9.72018 13 10 12.7202 10 12.375V11.4642C10.4508 11.204 10.8453 10.859 11.1623 10.4497C11.4327 10.1008 11.2649 9.61462 10.8692 9.41892C10.4735 9.22321 10.001 9.39943 9.68671 9.70947C9.49467 9.89893 9.27073 10.056 9.02321 10.1726C8.53235 10.4037 7.97768 10.4618 7.44958 10.3374C6.92149 10.2129 6.45109 9.91336 6.11502 9.48741C5.77896 9.06147 5.59704 8.53428 5.59891 7.99172C5.60078 7.44917 5.78633 6.92324 6.12532 6.49963C6.46431 6.07601 6.93677 5.77968 7.46571 5.65891C7.99465 5.53813 8.5489 5.60004 9.03816 5.83455C9.28487 5.9528 9.50772 6.11142 9.69846 6.3022C10.0106 6.61439 10.4819 6.79386 10.8789 6.60089C11.276 6.40792 11.4471 5.92295 11.1791 5.57212C10.8594 5.15346 10.459 4.8007 10 4.53575V3.625C10 3.27982 9.72018 3 9.375 3C9.02982 3 8.75 3.27982 8.75 3.625V4.07089C8.25712 3.97685 7.74827 3.97587 7.25024 4.07094V3.625C7.25024 3.27982 6.97042 3 6.62524 3Z" fill="currentColor"/></svg>,
                    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
                    'Pay with cryptocurrency'
                )}
            </div>
        </div>
    );

    return (
        <MultiViewModal
            maxWidth="sm"
            show={show}
            navBarClassName='px-4 pt-4'
            onClose={onClose}
            currentIdx={currentStep}
            onBack={() => setCurrentStep(0)}
        >
            <div data-title="Buy Credits" className="flow-root">
                {
                    message && (
                        <div className="p-1 mb-3 mt-3 text-sm text-yellow-800 rounded-lg bg-yellow-50 dark:bg-yellow-900/30 dark:text-yellow-200 mx-5">
                            <p className="text-center text-gray-900 dark:text-gray-100 mb-1 mt-2 text-sm">{message}</p>
                        </div>
                    )
                }
                {SelectHoursView}
            </div>
            <div data-title="Buy Credits" className="flow-root">
                <div className="flex flex-col items-center justify-center">
                    <h4 className="mt-4 text-3xl tracking-tight tabular-nums text-gray-900 dark:text-gray-100">${total.toFixed(2)}</h4>
                    <span className="mt-1 text-sm tabular-nums text-gray-500 dark:text-gray-400">
                        {effectiveHours || 0} hours · ${getHourRate(effectiveHours || 0).toFixed(2)}/hr
                    </span>
                </div>

                {PaymentMethodView}
            </div>
        </MultiViewModal>
    );
}
